import {AnalyticsDefinition} from '../../models/vte';

/**
 * Vehicle Detection is a single capability with mutually exclusive providers.
 * Zero or one of these keys may be active. Legacy + Eagle Eye V3 together is invalid.
 */
export const VEHICLE_DETECTION_ANALYTICS_KEYS = [
    'eagle_eye_v3_lpr_detection',
    'legacy_lpr_detection',
] as const;

export const DEFAULT_VEHICLE_DETECTION_ANALYTICS_KEY = 'eagle_eye_v3_lpr_detection';

const CUSTOMER_ANALYTICS_NAMES: Record<string, string> = {
    legacy_lpr_detection: 'Legacy LPR Detection',
    // Customer-facing: no "V3" branding. Backend key remains eagle_eye_v3_lpr_detection.
    eagle_eye_v3_lpr_detection: 'Eagle Eye LPR Detection',
    sticker_verification: 'Sticker Verification',
    tow_vehicle_detection: 'Tow Vehicle Detection',
};

export function isVehicleDetectionAnalyticsKey(analyticsKey: string | null | undefined): boolean {
    if (!analyticsKey) {
        return false;
    }
    return (VEHICLE_DETECTION_ANALYTICS_KEYS as readonly string[]).includes(analyticsKey);
}

/** Vision Analytics exclude Vehicle Detection providers. */
export function isVisionAnalyticsKey(analyticsKey: string | null | undefined): boolean {
    if (!analyticsKey) {
        return false;
    }
    return !isVehicleDetectionAnalyticsKey(analyticsKey);
}

export function hasVisionAnalytics(capabilities: string[]): boolean {
    return (capabilities || []).some((key) => isVisionAnalyticsKey(key));
}

/**
 * Prefer a single vehicle-detection key when resolving ambiguous display/read state.
 * Returns null when none is present (does not invent Eagle Eye V3).
 * When both are present, prefers Eagle Eye V3 for defensive display only.
 */
export function resolvePreferredVehicleDetectionKey(enabledKeys: string[]): string | null {
    const enabled = new Set(enabledKeys);
    if (enabled.has(DEFAULT_VEHICLE_DETECTION_ANALYTICS_KEY)) {
        return DEFAULT_VEHICLE_DETECTION_ANALYTICS_KEY;
    }
    if (enabled.has('legacy_lpr_detection')) {
        return 'legacy_lpr_detection';
    }
    return null;
}

/**
 * Return capabilities unchanged when at most one vehicle-detection key is present.
 * Does not insert a provider when neither is present.
 * Does not silently collapse dual Legacy+V3 selections (backend rejects those writes).
 */
export function normalizeVehicleDetectionCapabilities(capabilities: string[]): string[] {
    return [...(capabilities || [])];
}

/**
 * Historical name retained. Does NOT insert Eagle Eye V3 when none is selected.
 * Returns the capability list unchanged (zero or one vehicle provider both valid).
 */
export function ensureExactlyOneVehicleDetectionCapability(capabilities: string[]): string[] {
    return [...(capabilities || [])];
}

/**
 * Filter enabled definitions so "Currently enabled" never lists both LPR providers.
 */
export function filterExclusiveVehicleDetectionDefinitions<T extends Pick<AnalyticsDefinition, 'analytics_key'>>(
    definitions: T[],
): T[] {
    const keys = definitions.map((definition) => definition.analytics_key);
    const vehicleKeys = keys.filter((key) => isVehicleDetectionAnalyticsKey(key));
    if (vehicleKeys.length <= 1) {
        return [...definitions];
    }
    const preferred = resolvePreferredVehicleDetectionKey(vehicleKeys);
    let vehicleDetectionInserted = false;
    const filtered: T[] = [];
    for (const definition of definitions) {
        if (!isVehicleDetectionAnalyticsKey(definition.analytics_key)) {
            filtered.push(definition);
            continue;
        }
        if (vehicleDetectionInserted || !preferred) {
            continue;
        }
        if (definition.analytics_key === preferred) {
            filtered.push(definition);
            vehicleDetectionInserted = true;
        }
    }
    return filtered;
}

/**
 * @deprecated Vehicle Detection providers are listed distinctly (mutually exclusive).
 * Kept for catalog screens that still collapse historical duplicate labels.
 */
export function groupCustomerAnalyticsDefinitions<T extends Pick<AnalyticsDefinition, 'analytics_key'>>(
    definitions: T[],
    enabledKeys: string[] = [],
): T[] {
    return filterExclusiveVehicleDetectionDefinitions(
        definitions.filter((definition) => {
            if (!isVehicleDetectionAnalyticsKey(definition.analytics_key)) {
                return true;
            }
            const preferred = resolvePreferredVehicleDetectionKey(enabledKeys);
            return definition.analytics_key === preferred;
        }),
    );
}

/**
 * Toggle a specific vehicle-detection provider: clear siblings, then set or clear the key.
 * Disabling the active provider is allowed — zero vehicle providers is a valid state.
 * Enabling one provider clears the other (mutual exclusivity).
 */
export function toggleVehicleDetectionCapabilities(
    capabilities: string[],
    selectedKey: string,
    enabled: boolean,
): string[] {
    const next = new Set(capabilities);
    for (const key of VEHICLE_DETECTION_ANALYTICS_KEYS) {
        next.delete(key);
    }
    if (enabled && isVehicleDetectionAnalyticsKey(selectedKey)) {
        next.add(selectedKey);
    }
    return [...next];
}

export type VisionModeValue = 'disabled' | 'monitor_only' | 'enforced';

export const VISION_MODE_DISABLED_WHILE_ANALYTICS_MESSAGE =
    'Vision Mode cannot be Disabled while organization-level Vision capabilities remain enabled. Disable those Vision capabilities first, or keep Monitor Only / Enforced.';

/**
 * Apply Organization Vision Mode lifecycle against capability keys.
 * Returns the resolved mode and an optional validation error message.
 */
export function resolveVisionModeForCapabilities(
    capabilities: string[],
    requestedMode: VisionModeValue,
    modeExplicitlySet: boolean,
): { mode: VisionModeValue; error: string | null } {
    if (!hasVisionAnalytics(capabilities)) {
        return {mode: 'disabled', error: null};
    }
    if (requestedMode === 'disabled') {
        if (modeExplicitlySet) {
            return {mode: 'monitor_only', error: VISION_MODE_DISABLED_WHILE_ANALYTICS_MESSAGE};
        }
        return {mode: 'monitor_only', error: null};
    }
    if (requestedMode === 'monitor_only' || requestedMode === 'enforced') {
        return {mode: requestedMode, error: null};
    }
    return {mode: 'monitor_only', error: null};
}

export function customerVisionModeLifecycleHint(mode: string): string {
    switch ((mode || '').trim().toLowerCase()) {
        case 'disabled':
            return 'Automatic — Vision Mode is Disabled when no organization-level Vision capabilities are enabled.';
        case 'monitor_only':
            return 'Automatically enabled when organization-level Vision capabilities become active. Records verification without blocking access.';
        case 'enforced':
            return 'Manual administrator decision — verification rules apply to access decisions.';
        default:
            return '';
    }
}

const IMPLEMENTATION_PROVIDER_LABELS: Record<string, string> = {
    legacy_lpr: 'Legacy LPR',
    eagle_eye_v3_lpr: 'Eagle Eye V3 LPR',
    eeva: 'EEVA',
    eagle_eye: 'Eagle Eye',
};

const CUSTOMER_PROVIDER_LABELS: Record<string, string> = {
    legacy_lpr: 'Vehicle Detection',
    eagle_eye_v3_lpr: 'Vehicle Detection',
    eeva: 'KYRA Vision Verification',
    eagle_eye: 'Vision Service',
};

function titleCaseWords(value: string): string {
    return value
        .split(/[_\s-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');
}

export function implementationProviderLabel(providerId: string | null | undefined): string {
    if (!providerId?.trim()) {
        return '—';
    }
    const normalized = providerId.trim().toLowerCase();
    return IMPLEMENTATION_PROVIDER_LABELS[normalized] || titleCaseWords(providerId);
}

export function customerProviderLabel(providerId: string | null | undefined): string {
    if (!providerId?.trim()) {
        return '—';
    }
    const normalized = providerId.trim().toLowerCase();
    return CUSTOMER_PROVIDER_LABELS[normalized] || 'Vision Service';
}

export function formatProviderLabel(
    providerId: string | null | undefined,
    showImplementationDetails: boolean,
): string {
    return showImplementationDetails
        ? implementationProviderLabel(providerId)
        : customerProviderLabel(providerId);
}

export function configurationSourceHelp(source: string): string {
    switch (source) {
        case 'organization':
            // Legacy API value — OV-A1 removed org → camera inheritance.
            return 'Legacy source label. Cameras no longer inherit Organization Vision analytics.';
        case 'override':
            return 'Configured explicitly for this camera.';
        case 'none':
            return 'This camera has no analytics configuration. Enable analytics below and save to configure it.';
        case 'platform_default':
            return 'Legacy source label. Cameras no longer inherit silent platform-default analytics.';
        default:
            return '';
    }
}


export function customerAnalyticsName(definition: Pick<AnalyticsDefinition, 'analytics_key' | 'name'>): string {
    return CUSTOMER_ANALYTICS_NAMES[definition.analytics_key] || definition.name;
}

export function customerAnalyticsDescription(
    definition: Pick<AnalyticsDefinition, 'analytics_key' | 'description'>,
): string | null {
    if (!definition.description?.trim()) {
        return null;
    }
    return definition.description
        .replace(/Eagle Eye/gi, 'KYRA')
        .replace(/Legacy LPR/gi, 'standard detection')
        .replace(/LPR/gi, 'vehicle detection');
}

export function verificationProviderLabel(providerId: string, showImplementationDetails = false): string {
    if (!providerId) {
        return 'Not required';
    }
    if (showImplementationDetails) {
        return implementationProviderLabel(providerId);
    }
    return customerProviderLabel(providerId);
}

export function isImplementationProviderId(providerId: string | null | undefined): boolean {
    if (!providerId) {
        return false;
    }
    return Object.prototype.hasOwnProperty.call(
        IMPLEMENTATION_PROVIDER_LABELS,
        providerId.trim().toLowerCase(),
    );
}

export function customerVisionModeLabel(mode: string): string {
    switch (mode) {
        case 'disabled':
            return 'Disabled (Automatic)';
        case 'monitor_only':
            return 'Monitor Only (Automatic)';
        case 'enforced':
            return 'Enforced (Manual)';
        default:
            return mode.replace(/_/g, ' ');
    }
}

export function customerConfigurationSourceLabel(source: string): string {
    switch (source) {
        case 'organization':
            // Legacy API value — treat as unconfigured for display after OV-A1.
            return 'No analytics configured';
        case 'none':
            return 'No analytics configured';
        case 'platform_default':
            return 'No analytics configured';
        case 'override':
            return 'Camera configured';
        default:
            return source.replace(/_/g, ' ');
    }
}

/** Verification analytics use provider_event_type === 'analytics' (e.g. KYRA Vision). */
export const ANCHOR_TIME_CUSTOMER_HELP =
    'When the vision service reports the detection occurred at the camera.';

export const ANCHOR_TIME_IMPLEMENTATION_HELP =
    'Copied from the first attached provider event provider_timestamp (Eagle Eye startTimestamp for V3 LPR). This is when the provider reports the detection occurred at the camera.';

/** @deprecated Use anchorTimeHelp(showImplementationDetails) for customer-safe tooltips. */
export const ANCHOR_TIME_HELP = ANCHOR_TIME_CUSTOMER_HELP;

export const CREATED_TIME_HELP =
    'When KYRA created the VehicleTransaction record after correlating the first provider event.';

export const ANCHOR_CREATED_LIFECYCLE_CUSTOMER_HELP =
    'Lifecycle: camera capture → vision event timestamp → anchor time on the transaction → webhook received and correlated → transaction created. A gap between anchor time and created time is expected when event delivery or webhook processing is delayed.';

export const ANCHOR_CREATED_LIFECYCLE_IMPLEMENTATION_HELP =
    'Lifecycle: camera capture → provider event timestamp → anchor_timestamp on the transaction → webhook received and correlated → transaction created_at. A gap between anchor time and created time is expected when event delivery or webhook processing is delayed.';

/** @deprecated Use anchorCreatedLifecycleHelp(showImplementationDetails). */
export const ANCHOR_CREATED_LIFECYCLE_HELP = ANCHOR_CREATED_LIFECYCLE_CUSTOMER_HELP;

export function anchorTimeHelp(showImplementationDetails: boolean): string {
    return showImplementationDetails ? ANCHOR_TIME_IMPLEMENTATION_HELP : ANCHOR_TIME_CUSTOMER_HELP;
}

export function createdTimeHelp(): string {
    return CREATED_TIME_HELP;
}

export function anchorCreatedLifecycleHelp(showImplementationDetails: boolean): string {
    return showImplementationDetails
        ? ANCHOR_CREATED_LIFECYCLE_IMPLEMENTATION_HELP
        : ANCHOR_CREATED_LIFECYCLE_CUSTOMER_HELP;
}

export function deriveVerificationProviderFromCapabilities(
    definitions: AnalyticsDefinition[],
    capabilities: string[],
): string {
    const capabilitySet = new Set(capabilities);
    const providers = new Set(
        definitions
            .filter(
                (definition) =>
                    definition.is_active
                    && capabilitySet.has(definition.analytics_key)
                    && definition.provider_event_type === 'analytics'
                    && definition.provider?.trim(),
            )
            .map((definition) => definition.provider.trim()),
    );
    if (providers.size !== 1) {
        return '';
    }
    return [...providers][0];
}
